/**
 * Atributos del tema que NO cambian entre temas (comunes a todos).
 * Usar con createTheme(themeBase, { palette: {...}, components: {...} }) en cada CustomTheme*.
 * dark:
 *  label de los switch, checkboxes, radio.
 */
import { alpha, createTheme, Theme, ThemeOptions } from "@mui/material";
import type { CustomExpedienteButton, CustomUploadFile } from "@interfaces/theme";

/** Breakpoints por defecto de MUI para tipografía responsive (sin depender del tema en construcción). */
const muiDefaultBreakpoints = createTheme();

export const getExpedienteButtonColors = (theme: Theme): CustomExpedienteButton => {
  const config = theme.palette.components?.expedienteButton;

  const pendingBg = config?.pending?.bg ?? theme.palette.primary.main;
  const pendingText = config?.pending?.text ?? "#FFFFFF";
  const completeBg = config?.complete?.bg ?? theme.palette.primary.dark;
  const completeText = config?.complete?.text ?? theme.palette.primary.light;
  const validatedBg = config?.validated?.bg ?? theme.palette.primary.light;
  const validatedText = config?.validated?.text ?? theme.palette.primary.main;
  const incidentBg = config?.incident?.bg ?? theme.palette.primary.accent;
  const incidentText = config?.incident?.text ?? "#FFFFFF";

  return {
    pending: {
      bg: pendingBg,
      text: pendingText,
      hoverBg: config?.pending?.hoverBg ?? pendingBg,
      hoverText: config?.pending?.hoverText ?? pendingText,
    },
    complete: {
      bg: completeBg,
      text: completeText,
      hoverBg: config?.complete?.hoverBg ?? completeBg,
      hoverText: config?.complete?.hoverText ?? completeText,
    },
    validated: {
      bg: validatedBg,
      text: validatedText,
      hoverBg: config?.validated?.hoverBg ?? validatedBg,
      hoverText: config?.validated?.hoverText ?? validatedText,
    },
    incident: {
      bg: incidentBg,
      text: incidentText,
      hoverBg: config?.incident?.hoverBg ?? incidentBg,
      hoverText: config?.incident?.hoverText ?? incidentText,
    },
  };
};

/** Adapter: misma paleta que ButtonExpedienteStatus (expedienteButton). */
export const getUploadFileColors = (theme: Theme): CustomUploadFile => {
  const { pending, complete, validated, incident } = getExpedienteButtonColors(theme);

  return {
    pending,
    filled: complete,
    validated,
    rejected: incident,
  };
};

/**
 * Hover de los botones con fondo `primary.main`: si la marca define `primary.hover` se usa ese
 * fondo con texto blanco; si no, el comportamiento de siempre (fondo `dark` y `fallbackText`).
 */
export const getPrimaryHoverStyles = (theme: Theme, fallbackText: string) =>
  theme.palette.primary.hover
    ? { backgroundColor: theme.palette.primary.hover, color: "#FFF" }
    : { backgroundColor: theme.palette.primary.dark, color: fallbackText };

const expedienteBaseStyles = {
  paddingLeft: "10px",
  paddingRight: "10px",
  width: "150px",
  height: "30px",
  fontSize: 15,
} as const;

const expedienteSmallStyles = {
  width: "90px",
  height: "23px",
  fontSize: 13,
} as const;

export const themeBase: ThemeOptions = createTheme({
  palette: {
    /**
     * Gris de toda línea separadora: el `<Divider>` sin `sx` y cualquier `borderColor: "divider"`.
     * Sustituye al `rgba(0, 0, 0, 0.12)` que trae MUI por default. Ningún tenant lo sobrescribe
     * hoy, pero puede hacerlo en su `createTheme(themeBase, …)`.
     */
    divider: "#E4E7EC",
    fontWeights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    tokens: {
      warningSoftBg: "#FEF4E4",
      warningSoftBorder: "#F9EBD2",
      neutralSoftBg: "#E2E5E8",
      neutralSoftBorder: "#CCD1D7",
      // Segundo gris de línea del diseño (`--ld-border-2`), un tono más firme que `divider`: el
      // borde de una card levantada, los dropzones punteados y los controles sin seleccionar.
      borderStrong: "#D6DBE3",
      // Borde de un input en hover (`--ld-blue-500` del diseño). Es el azul de Lendia aunque la marca tenga otro
      // color: el prototipo de Mercader lo hereda así; la marca que quiera otro lo pone en `neutrals.borderHover`.
      borderHover: "#6FA1E2",
      // Neutros de texto del diseño (`--ld-text`, `--ld-text-2`, `--ld-text-3`). Por ahora solo los
      // usan los componentes del cotizador v2: `palette.text` sigue siendo el default de MUI.
      textPrimary: "#0E1F3A",
      textSecondary: "#4A5567",
      textTertiary: "#7A8497",
      // Neutros de superficie del wizard v2; la marca los cambia con `components.neutrals`.
      surface2: "#F4F6F9",
      pageBg: "#F6F8FB",
    },
  },
  typography: {
    fontFamily: ["Outfit"].join(","),
    h1: {
      fontSize: 39,
      fontWeight: 300,
    },
    h2: {
      fontSize: 31,
      fontWeight: 300,
    },
    h3: {
      fontSize: 25,
      fontWeight: 300,
    },
    h4: {
      fontSize: 20,
      fontWeight: 300,
    },
    h5: {
      fontSize: 16,
      fontWeight: 300,
    },
    body1: {
      fontSize: 16,
      fontWeight: 300,
    },
    body2: {
      fontSize: 13,
      fontWeight: 300,
    },
    body3: {
      fontSize: 11,
      fontWeight: 300,
      fontFamily: "Outfit",
    },
    tofu_title: {
      fontSize: 20,
      fontWeight: 500,
      fontFamily: "Outfit",
      textAlign: "center",
      [muiDefaultBreakpoints.breakpoints.up("md")]: {
        fontSize: 25,
      },
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          tofu_title: "h3",
        },
      },
      variants: [
        {
          props: { variant: "tofu_title" },
          style: ({ theme }: { theme: Theme }) => ({
            color: theme.palette.primary.dark,
          }),
        },
      ],
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.primary.light,
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.primary.light,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: "#FF6060",
        },
      },
    },
    MuiPaper: {
      variants: [
        // para la v2 — spec de `.card` del onboarding (Claude Design): borde 1px, radio 14px, sombra xs.
        {
          props: { variant: "lendia_paper" },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 14,
            boxShadow: "0 1px 2px rgba(14, 31, 58, 0.05)",
            padding: 28,
            // La app no monta `CssBaseline`, así que no hay `box-sizing: border-box` global: sin
            // esto, una card con `height: 100%` suma su padding y su borde al alto de la fila del
            // grid, se desborda sobre la siguiente y se come el `gap`.
            boxSizing: "border-box",
          }),
        },
      ],
    },
    MuiCircularProgress: {
      styleOverrides: {
        determinate: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.primary.base,
        }),
        indeterminate: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "14px",
          boxShadow: "8px 4px 20px 0px rgba(82, 140, 214, 0.25)",
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          // backgroundColor: "rgba(0, 0, 0, 0.5)",
          // border: "2px red solid"
          // ".aria-labelledby": {
          //   border: "2px blue solid"
          // }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "10px 25px",
          textTransform: "none",
          fontSize: 16,
          fontWeight: 700,
        },
      },
      variants: [
        {
          props: {
            variant: "default",
          },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: "#FFF",
            "&.Mui-disabled": {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.4,
              color: "#FFF",
            },
            "&:hover": getPrimaryHoverStyles(theme, "#A3D4E8"),
            "&.MuiButton-defaultSizeSmall": {
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
            },
          }),
        },
        {
          props: {
            variant: "default_disabled",
          },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.primary.main,
            opacity: 0.4,
            color: "#FFF",
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
              color: "#FFF",
            },
            "&.MuiButton-defaultSizeSmall": {
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
            },
          }),
        },
        {
          props: {
            variant: "blue_outlined",
          },
          style: ({ theme }: { theme: Theme }) => ({
            color: theme.palette.primary.dark,
            backgroundColor: "#FFF",
            border: `2px solid ${theme.palette.primary.dark}`,
            lineHeight: "22px",
            "&.Mui-disabled": {
              color: "#bdbdbd",
              border: "2px solid #bdbdbd",
              backgroundColor: "transparent",
            },
            "&.MuiButton-blue_outlinedSizeSmall": {
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
            },
          }),
        },
        {
          props: {
            variant: "contained",
          },
          style: ({ theme }: { theme: Theme }) => ({
            "&.Mui-disabled": {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.4,
              color: "#FFF",
            },
          }),
        },
        {
          props: {
            variant: "expediente",
          },
          style: ({ theme }: { theme: Theme }) => ({
            paddingLeft: "10px",
            paddingRight: "10px",
            width: "150px",
            height: "30px",
            fontSize: 15,
            backgroundColor: theme.palette.primary.main,
            color: "#FFFFFF",
            "&.Mui-disabled": {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.4,
              color: "#FFFFFF",
            },
            "&:hover": getPrimaryHoverStyles(theme, theme.palette.primary.light),
          }),
        },
        {
          props: {
            variant: "expediente",
            size: "small",
          },
          style: expedienteSmallStyles,
        },
        {
          props: {
            variant: "expediente_pending",
          },
          style: ({ theme }: { theme: Theme }) => {
            const { pending } = getExpedienteButtonColors(theme);
            return {
              ...expedienteBaseStyles,
              backgroundColor: pending.bg,
              color: pending.text,
              "&.Mui-disabled": {
                backgroundColor: pending.bg,
                opacity: 0.4,
                color: pending.text,
              },
              "&:hover": {
                backgroundColor: pending.hoverBg,
                color: pending.hoverText,
              },
            };
          },
        },
        {
          props: {
            variant: "expediente_pending",
            size: "small",
          },
          style: expedienteSmallStyles,
        },
        {
          props: {
            variant: "expediente_filled",
          },
          style: ({ theme }: { theme: Theme }) => {
            const { complete } = getExpedienteButtonColors(theme);
            return {
              ...expedienteBaseStyles,
              backgroundColor: complete.bg,
              color: complete.text,
              "&.Mui-disabled": {
                backgroundColor: complete.bg,
                opacity: 0.4,
                color: complete.text,
              },
              "&:hover": {
                backgroundColor: complete.hoverBg,
                color: complete.hoverText,
              },
            };
          },
        },
        {
          props: {
            variant: "expediente_filled",
            size: "small",
          },
          style: expedienteSmallStyles,
        },
        {
          props: {
            variant: "expediente_validated",
          },
          style: ({ theme }: { theme: Theme }) => {
            const { validated } = getExpedienteButtonColors(theme);
            return {
              ...expedienteBaseStyles,
              backgroundColor: validated.bg,
              color: validated.text,
              "&.Mui-disabled": {
                backgroundColor: validated.bg,
                color: validated.text,
                opacity: 1,
              },
              "&:hover": {
                backgroundColor: validated.hoverBg,
                color: validated.hoverText,
              },
            };
          },
        },
        {
          props: {
            variant: "expediente_validated",
            size: "small",
          },
          style: expedienteSmallStyles,
        },
        {
          props: {
            variant: "expediente_rejected",
          },
          style: ({ theme }: { theme: Theme }) => {
            const { incident } = getExpedienteButtonColors(theme);
            return {
              ...expedienteBaseStyles,
              width: "160px",
              backgroundColor: incident.bg,
              color: incident.text,
              "&.Mui-disabled": {
                backgroundColor: incident.bg,
                opacity: 0.4,
                color: incident.text,
              },
              "&:hover": {
                backgroundColor: incident.hoverBg,
                color: incident.hoverText,
              },
            };
          },
        },
        {
          props: {
            variant: "expediente_rejected",
            size: "small",
          },
          style: {
            ...expedienteSmallStyles,
            width: "150px",
          },
        },
        {
          props: {
            variant: "yellow",
          },
          style: {
            backgroundColor: "#FF9D14",
            color: "#FFF",
            "&.Mui-disabled": {
              backgroundColor: "#FF9D14",
              opacity: 0.4,
              color: "#FFF",
            },
            "&:hover": {
              backgroundColor: "#FF9D14",
              color: "#FFF",
            },
            "&.MuiButton-defaultSizeSmall": {
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
            },
          },
        },
        {
          props: {
            variant: "green",
          },
          style: {
            color: "#FFFFFF",
            backgroundColor: "#00BB2D",
            "&.Mui-disabled": {
              backgroundColor: "#009A24",
              opacity: 0.4,
            },
            "&:hover": {
              backgroundColor: "#009A24",
            },
          },
        },

        // para la v2
        {
          props: {
            variant: "default_v2",
          },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: "#FFF",
            "&.Mui-disabled": {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.4,
              color: "#FFF",
            },
            "&:hover": getPrimaryHoverStyles(theme, "#A3D4E8"),
            "&.MuiButton-defaultSizeSmall": {
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
            },
          }),
        },
      ],
    },
    // Esto aplica el color al contenedor (<label>) de: Checkbox, Radio, Switch
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.primary.dark,
          "&.Mui-disabled": {
            opacity: 0.4,
            cursor: "not-allowed",
          },
        }),
      },
    },
    // color del label para los inputs, selects.
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.primary.dark,
        }),
        asterisk: {
          color: "#CD0202",
        },
      },
    },
    // Estilos para los inputs y selects.
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 4,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.light,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.light,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#CD0202",
          },
        }),
        notchedOutline: ({ theme }: { theme: Theme }) => ({
          borderColor: theme.palette.primary.light,
          borderRadius: 4,
        }),
      },
      variants: [
        // para la v2 — spec del onboarding (`.input` del diseño): alto 42px, radio 8px, borde 1px fijo (no engrosa
        // en focus). Mismo comportamiento para todas las marcas: borde gris (`tokens.borderStrong`), hover en
        // `tokens.borderHover` (azul de Lendia salvo que la marca lo cambie) y focus en el color de marca con un halo de
        // 3px al 16%. Sin esto el borde heredaba `primary.light` de la regla base de arriba.
        {
          props: { size: "lendia_input" },
          style: ({ theme }: { theme: Theme }) => ({
            height: 42,
            borderRadius: 8,
            transition: "box-shadow .15s ease",
            "& .MuiOutlinedInput-input": {
              height: "100%",
              padding: "0 14px",
              boxSizing: "border-box",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: 8,
              borderWidth: 1,
              borderColor: theme.palette.tokens?.borderStrong,
              transition: "border-color .15s ease",
            },
            "&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.tokens?.borderHover,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: theme.palette.primary.main,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "#CD0202",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.tokens?.surface2,
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.tokens?.borderStrong,
            },
          }),
        },
      ],
    },
    // Estilos para el progress bar.
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          height: 10,
          borderRadius: 5,
          backgroundColor: theme.palette.components?.progressBar?.rail || "#d9d9d9",
          boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.2)",
        }),
        bar: ({ theme }: { theme: Theme }) => ({
          borderRadius: 5,
          backgroundColor: theme.palette.components?.progressBar?.track || theme.palette.primary.dark,
        }),
      },
    },

    // MuiTextField: {
    //   styleOverrides: {
    //     root: ({ theme }: { theme: Theme }) => ({
    //       "& .MuiOutlinedInput-root": {
    //         borderRadius: "10px",
    //       },
    //       "& .MuiFilledInput-root": {
    //         borderRadius: "10px",
    //       },
    //       "& .MuiInputBase-root": {
    //         borderRadius: "10px",
    //       },
    //       "& .MuiOutlinedInput-notchedOutline": {
    //         borderColor: theme.palette.primary.light,
    //       },
    //       "& .MuiInput-underline::before": {
    //         borderColor: theme.palette.primary.light,
    //       },
    //       "& .MuiFilledInput-underline::before": {
    //         borderColor: theme.palette.primary.light,
    //       },
    //       "& .MuiInputLabel-asterisk": {
    //         color: "#CD0202",
    //       },
    //     }),
    //   },
    // },
    // MuiButton: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: 6,
    //       padding: "10px 25px",
    //       textTransform: "none",
    //       fontSize: 16,
    //       fontWeight: 700,
    //     },
    //   },
    //   variants: [
    //     {
    //       props: {
    //         variant: "default",
    //       },
    //       style: ({ theme }: { theme: Theme }) => ({
    //         backgroundColor: theme.palette.primary.main,
    //         color: "#FFF",
    //         "&.Mui-disabled": {
    //           backgroundColor: theme.palette.primary.main,
    //           opacity: 0.4,
    //           color: "#FFF",
    //         },
    //         "&:hover": {
    //           backgroundColor: theme.palette.primary.dark,
    //           color: theme.palette.primary.light,
    //         },
    //         "&.MuiButton-defaultSizeSmall": {
    //           fontSize: 11,
    //           fontWeight: 600,
    //           padding: "2px 8px",
    //         },
    //       }),
    //     },
    //     {
    //       props: {
    //         variant: "default_disabled",
    //       },
    //       style: {
    //         backgroundColor: "#D45D00",
    //         opacity: 0.4,
    //         color: "#FFF",
    //         // "&.Mui-disabled": {
    //         //   backgroundColor: "#528CD6",
    //         //   opacity: 0.4,
    //         //   color: "#FFF",
    //         // },
    //         "&:hover": {
    //           backgroundColor: "#D45D00",
    //           color: "#FFF",
    //         },
    //         "&.MuiButton-defaultSizeSmall": {
    //           fontSize: 11,
    //           fontWeight: 600,
    //           padding: "2px 8px",
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: "contained",
    //       },
    //       style: {
    //         "&.Mui-disabled": {
    //           backgroundColor: "#D45D00",
    //           opacity: 0.4,
    //           color: "#FFF",
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: "orange",
    //       },
    //       style: {
    //         backgroundColor: "#F2704F",
    //         color: "#FFF",
    //         "&.Mui-disabled": {
    //           backgroundColor: "#F2704F",
    //           opacity: 0.4,
    //         },
    //         "&:hover": {
    //           backgroundColor: "#F2704F",
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: "black",
    //       },
    //       style: {
    //         backgroundColor: "#2C2C2C",
    //         color: "#FFF",
    //         "&.Mui-disabled": {
    //           backgroundColor: "#2C2C2C",
    //           opacity: 0.4,
    //         },
    //         "&:hover": {
    //           backgroundColor: "#2C2C2C",
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: "blue_outlined",
    //       },
    //       style: {
    //         color: "#333F48",
    //         backgroundColor: "#FFF",
    //         border: "2px solid #333F48",
    //         lineHeight: "22px",
    //         "&.Mui-disabled": {
    //           color: "#bdbdbd",
    //           border: "2px solid #bdbdbd",
    //           backgroundColor: "transparent",
    //         },
    //         "&.MuiButton-blue_outlinedSizeSmall": {
    //           fontSize: 11,
    //           fontWeight: 600,
    //           padding: "2px 8px",
    //         },
    //       },
    //     },
    //     {
    //       props: {
    //         variant: "expediente_validado",
    //       },
    //       style: ({ theme }: { theme: Theme }) => ({
    //         width: "150px",
    //         height: "30px",
    //         fontSize: 15,
    //         color: theme.palette.primary.main,
    //         backgroundColor: theme.palette.primary.light,
    //         "&.Mui-disabled": {
    //           backgroundColor: theme.palette.primary.light,
    //           opacity: 0.4,
    //         },
    //         "&:hover": {
    //           backgroundColor: theme.palette.primary.light,
    //         },
    //       }),
    //     },
    //     {
    //       props: {
    //         variant: "expediente_validado",
    //         size: "small",
    //       },
    //       style: {
    //         width: "90px",
    //         height: "23px",
    //         fontSize: 13,
    //       },
    //     },
    //     {
    //       props: {
    //         variant: "expediente",
    //       },
    //       style: ({ theme }: { theme: Theme }) => ({
    //         paddingLeft: "10px",
    //         paddingRight: "10px",
    //         width: "150px",
    //         height: "30px",
    //         fontSize: 15,
    //         backgroundColor: theme.palette.primary.main,
    //         color: "#FFF",
    //         "&.Mui-disabled": {
    //           backgroundColor: theme.palette.primary.main,
    //           opacity: 0.4,
    //           color: "#FFF",
    //         },
    //         "&:hover": {
    //           backgroundColor: theme.palette.primary.dark,
    //           color: theme.palette.primary.light,
    //         },
    //       }),
    //     },
    //     {
    //       props: {
    //         variant: "expediente",
    //         size: "small",
    //       },
    //       style: {
    //         width: "90px",
    //         height: "23px",
    //         fontSize: 13,
    //       },
    //     },
    //   ],
    // },
    // MuiFormControl: {
    //   styleOverrides: {
    //     root: ({ theme }: { theme: Theme }) => ({
    //       "& .MuiInputLabel-asterisk": {
    //         color: "#CD0202",
    //       },
    //       "& .MuiOutlinedInput-root": {
    //         "& fieldset": {
    //           borderColor: theme.palette.primary.light,
    //           borderRadius: "5px",
    //         },
    //         "&:hover fieldset": {
    //           borderColor: theme.palette.primary.light,
    //         },
    //         "&.Mui-focused fieldset": {
    //           borderColor: theme.palette.primary.light,
    //         },
    //       },
    //       "& .MuiSelect-icon": {
    //         color: theme.palette.primary.light,
    //       },
    //     }),
    //   },
    // },
    // MuiSelect: {
    //   styleOverrides: {
    //     root: ({ theme }: { theme: Theme }) => ({
    //       "& .MuiOutlinedInput-notchedOutline": {
    //         borderColor: theme.palette.primary.light,
    //       },
    //     }),
    //   },
    // },
    // MuiMenu: {
    //   styleOverrides: {
    //     paper: {
    //       borderRadius: "14px",
    //       boxShadow: "8px 4px 20px 0px rgba(82, 140, 214, 0.25)",
    //     },
    //   },
    // },
    // MuiModal: {
    //   styleOverrides: {
    //     root: {
    //       // backgroundColor: "rgba(0, 0, 0, 0.5)",
    //       // border: "2px red solid"
    //       // ".aria-labelledby": {
    //       //   border: "2px blue solid"
    //       // }
    //     },
    //   },
    // },
    // MuiFormControlLabel: {
    //   styleOverrides: {
    //     root: ({ theme }: { theme: Theme }) => ({
    //       color: theme.palette.primary.dark,
    //     }),
    //   },
    // },
  },
});
